import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Plus, ChevronDown, Send, MessageSquare, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { GlassCard, PageHeader, Modal, Spinner, EmptyState } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";

const FAQ = [
  { q: "Como começo a usar a plataforma?", a: "Crie sua instância em Meus Bots com o token do @BotFather, importe seu conteúdo pelo bot (link direto ou lista M3U do seu fornecedor) e gere acessos com usuário e senha na aba Acessos. É só enviar o link da playlist para o cliente." },
  { q: "Como funciona o teste grátis?", a: "Toda conta nova ganha 30 GB de espaço NVMe por 15 dias, sem cartão. Ao final do teste, o streaming é bloqueado automaticamente até assinar um plano." },
  { q: "Como funcionam os planos?", a: "Mensalidade fixa pelo espaço de armazenamento (250 GB a 10 TB, e sob medida acima disso). Cada pagamento soma +30 dias de validade — renovar antes do vencimento acumula." },
  { q: "O streaming é ilimitado mesmo?", a: "Sim. Nenhum plano desconta nada por GB assistido: seus clientes podem assistir à vontade, sem surpresa na fatura." },
  { q: "O que acontece quando meu plano vence?", a: "O streaming dos seus links é bloqueado automaticamente (erro 402) e os uploads pausam. Recebe avisos no Telegram 5, 3 e 1 dia antes, no dia do vencimento e no dia seguinte. Ao renovar, tudo reativa na hora." },
  { q: "Como importo minha lista M3U?", a: "Pelo seu bot no Telegram: envie o link get.php do seu fornecedor ou o arquivo .m3u. O bot baixa, sobe para o storage NVMe e cataloga tudo automaticamente com capas e sinopses." },
  { q: "Como meus clientes assistem?", a: "Na aba Acessos da sua instância, gere credenciais (usuário e senha) e envie o link da playlist. Funciona em qualquer player de IPTV (celular, TV Box, Smart TV)." },
  { q: "Posso pausar o acesso de um cliente?", a: "Sim — na aba Acessos, use o botão de pausa. O vídeo do cliente para em até 10 segundos, mesmo no meio da reprodução." },
  { q: "Posso usar meu próprio domínio?", a: "Sim! Em Ajustes → Domínio aponta o seu domínio (via Cloudflare) e todos os seus links de playlist e streaming saem com a sua marca." },
  { q: "Quantos bots posso criar?", a: "Cada conta começa com limite de 1 bot. Se precisar de mais, abra um ticket aqui mesmo que o administrador aumenta seu limite." },
  { q: "O que é o Sincronizador?", a: "É a nossa ferramenta parceira (wxsyncvods.shop) que sobe conteúdo automaticamente para a sua plataforma, sem precisar de importar nada manualmente." },
];

const STATUS = {
  open: { label: "Aguardando resposta", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  answered: { label: "Respondido", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  closed: { label: "Fechado", cls: "text-slate-500 border-slate-700 bg-slate-800/40" },
};

const fmtDateTime = (iso) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800/60 last:border-0" data-testid={`faq-item-${i}`}>
      <button onClick={() => setOpen(!open)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
        <span className="text-sm text-white font-medium">{q}</span>
        <ChevronDown size={15} className={`text-cyan-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-xs text-slate-400 leading-relaxed" data-testid={`faq-answer-${i}`}>{a}</p>}
    </div>
  );
}

function TicketCard({ t, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const s = STATUS[t.status] || STATUS.open;

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${t.id}/reply`, { message: reply.trim() });
      setReply("");
      toast.success("Resposta enviada");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const close = async () => {
    setSending(true);
    try {
      await api.post(`/tickets/${t.id}/close`);
      toast.success("Ticket fechado");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-800/60 bg-slate-950/40" data-testid={`ticket-card-${t.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`ticket-toggle-${t.id}`} className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left">
        <MessageSquare size={16} className="text-cyan-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold truncate">{t.subject}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{fmtDateTime(t.updated_at)} · {t.messages.length} {t.messages.length === 1 ? "mensagem" : "mensagens"}</p>
        </div>
        <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${s.cls}`} data-testid={`ticket-status-${t.id}`}>{s.label}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-slate-800/60 pt-4">
          {t.messages.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-4 py-3 text-xs leading-relaxed ${m.author === "admin" ? "ml-auto bg-cyan-400/10 border border-cyan-400/25 text-cyan-100" : "bg-slate-800/60 border border-slate-700/60 text-slate-200"}`} data-testid={`ticket-msg-${m.id}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              <p className={`text-[9px] font-mono2 uppercase tracking-widest mt-1.5 ${m.author === "admin" ? "text-cyan-400/70" : "text-slate-500"}`}>
                {m.author === "admin" ? "Suporte" : "Você"} · {fmtDateTime(m.created_at)}
              </p>
            </div>
          ))}
          {t.status !== "closed" && (
            <div className="flex gap-2">
              <input
                data-testid={`ticket-reply-input-${t.id}`}
                className="input-dark flex-1 rounded-xl px-4 py-2.5 text-sm"
                placeholder="Escreva sua resposta..."
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <button data-testid={`ticket-reply-send-${t.id}`} onClick={send} disabled={sending || !reply.trim()} className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-1.5 shrink-0">
                {sending ? <Spinner /> : <Send size={13} />} Enviar
              </button>
              <button data-testid={`ticket-close-${t.id}`} onClick={close} disabled={sending} title="Fechar ticket" className="btn-ghost-dark px-3 py-2.5 rounded-xl text-xs text-slate-400 shrink-0">
                <XCircle size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Suporte() {
  const [tickets, setTickets] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/tickets/mine").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/tickets", { subject: subject.trim(), message: message.trim() });
      toast.success("Ticket aberto! Será avisado no Telegram quando o suporte responder.");
      setShowNew(false);
      setSubject("");
      setMessage("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="suporte-page">
      <PageHeader eyebrow="Central de ajuda" title="Suporte" subtitle="Tire dúvidas no FAQ ou abra um ticket — respondemos e é avisado no Telegram." />
      <TutorialHint
        id="suporte"
        title="Como funciona o suporte"
        steps={[
          "O FAQ responde as dúvidas mais comuns na hora — toque na pergunta para ver o passo a passo.",
          "Não achou a resposta? Abra um Novo ticket, descreva o problema e acompanhe a conversa aqui mesmo.",
          "Quando o suporte responde, é avisado no seu Telegram (se estiver vinculado).",
          "Depois de resolvido, pode fechar o ticket — e reabrir quando precisar.",
        ]}
      />

      <GlassCard className="overflow-hidden anim-fade-up" data-testid="faq-card">
        <div className="px-5 pt-5 pb-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
            <LifeBuoy size={18} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Perguntas frequentes</h3>
            <p className="text-[11px] text-slate-500">As dúvidas mais comuns, respondidas direto</p>
          </div>
        </div>
        <div>
          {FAQ.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
        </div>
      </GlassCard>

      <GlassCard className="p-6 anim-fade-up" data-testid="tickets-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Meus tickets</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Não achou sua dúvida no FAQ? Abra um ticket.</p>
          </div>
          <button data-testid="new-ticket-btn" onClick={() => setShowNew(true)} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Novo ticket
          </button>
        </div>
        {tickets === null ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : tickets.length === 0 ? (
          <EmptyState icon={MessageSquare} title="Nenhum ticket aberto" subtitle="Quando precisar de ajuda, é só abrir um ticket aqui." testid="tickets-empty" />
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((t) => <TicketCard key={t.id} t={t} onChanged={load} />)}
          </div>
        )}
      </GlassCard>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Abrir ticket de suporte" testid="new-ticket-modal">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Assunto</label>
            <input data-testid="ticket-subject-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex: Não consigo importar minha lista" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Mensagem</label>
            <textarea data-testid="ticket-message-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm min-h-[120px] resize-y" placeholder="Descreva o que está acontecendo com o máximo de detalhes..." value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000} />
          </div>
          <button data-testid="ticket-create-submit" onClick={create} disabled={creating || subject.trim().length < 3 || message.trim().length < 3} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
            {creating ? <Spinner /> : <><Send size={14} /> Enviar ticket</>}
          </button>
        </div>
      </Modal>
    </div>
  );
}
